import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchSiteConfig } from '../api/client';
import type { PublicAds, SiteConfig, SiteLimitsPublic } from '../api/types';

/**
 * このサイトで使える機能と広告（doc/ADS.md §1）。**同じビルドを、サーバの環境変数だけで別のサイトとして出す**ためのもの。
 *
 * 本番は API サーバが index.html に `window.__GENDAN_CONFIG__` を埋め込むので、読み込みを待たずに決まる。
 * 埋め込みが無いとき（`npm run dev` で Vite が index.html を配るとき）だけ `GET /v1/config` を叩く。
 * それまでは広告を「無い」として描く（切ったはずの広告が一瞬出るより、あとから出るほうがよい）。
 */
declare global {
  interface Window {
    __GENDAN_CONFIG__?: SiteConfig;
    adsbygoogle?: unknown[];
  }
}

const NOTHING: SiteConfig = { features: { ads: false }, ads: null };

/** サーバが値を返さないとき（読み込み前・古いサーバ）の既定値。services/api の lib/limits.ts・handlers/contact.ts の既定と揃える。 */
export const DEFAULT_LIMITS: SiteLimitsPublic = { anonThemeDailyLimit: 3, userImageDailyLimit: 3, contactDailyLimit: 5, userCompatDailyLimit: 3, userCompatImageDailyLimit: 3 };

interface SiteState {
  /** 出す広告。広告を切ったサイトでは null。 */
  readonly ads: PublicAds | null;
  /** 1 人あたりの回数。ヘルプ・規約・ボタンの文言はこれを出す（管理メニューで変わるので固定で書かない）。 */
  readonly limits: SiteLimitsPublic;
}

const SiteContext = createContext<SiteState>({ ads: null, limits: DEFAULT_LIMITS });

const stateOf = (config: SiteConfig): SiteState => ({
  ads: config.features.ads ? config.ads : null,
  limits: config.limits ?? DEFAULT_LIMITS,
});

/**
 * 「1 日 3 回」のような書き方。上限なしなら「回数の上限なし」。
 * 0 は管理メニューで止めているとき。`zero` に場面に合った言い方を渡す（既定は「0 回。止めています」）。
 */
export const perDay = (limit: number | null, unit = '回', zero = `0 ${unit}。止めています`): string =>
  limit === null ? '回数の上限なし' : limit === 0 ? zero : `1 日 ${limit} ${unit}`;

/** 「1 日 3 回まで」。上限なしなら「回数の上限なし」、0 なら「いまは止めています」。 */
export const perDayMax = (limit: number | null, unit = '回'): string =>
  limit === null ? '回数の上限なし' : limit === 0 ? 'いまは止めています' : `1 日 ${limit} ${unit}まで`;

export function SiteProvider({ children }: { readonly children: ReactNode }) {
  const [state, setState] = useState<SiteState>(() => stateOf(window.__GENDAN_CONFIG__ ?? NOTHING));

  useEffect(() => {
    if (window.__GENDAN_CONFIG__) return;
    let cancelled = false;
    void fetchSiteConfig()
      .then((config) => { if (!cancelled) setState(stateOf(config)); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  return <SiteContext.Provider value={state}>{children}</SiteContext.Provider>;
}

export const useSite = (): SiteState => useContext(SiteContext);
